import ManageRoutes from '@/router/ManageRoutes';
import { AxiosResponse } from 'axios';
import { Theme } from 'klayowebshared';
import { get, trim } from 'lodash';
import React, { SyntheticEvent } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppContext } from '../../common/AppContext';
import axiosClient from '../../common/AxiosClient';
import { Utils } from '../../common/Utils';
import { ErrorModel } from '../../common/models/ErrorModel';
import { Alert } from '../../components/Alert';
import { View } from '../../components/View';
import { Data } from '../../data/Data';
import { Organization } from '../../data/Organization';
import { BillingInvoices } from '../../data/billing/BillingInvoices';
import { BillingPlans } from '../../data/billing/BillingPlans';
import { GroupMemberSelectorList } from '../../data/filters/GroupMemberSelectorList';
import { BillingSettings } from '../../data/settings/BillingSettings';
import { OrganizationSettings } from '../../data/settings/OrganizationSettings';
import { SettingCourseFacultyList } from '../../data/settings/SettingCourseFacultyList';
import { SettingsCourseList } from '../../data/settings/SettingCourseList';
import { SettingsRegulationCategoryList } from '../../data/settings/SettingRegulationCategoryList';
import { SettingsRegulationList } from '../../data/settings/SettingRegulationList';
import { SettingsRegulationRegionList } from '../../data/settings/SettingRegulationRegionList';
import { SettingsAttribute } from '../../data/settings/SettingsAttribute';
import { SettingsAttributeCategory } from '../../data/settings/SettingsAttributeCategory';
import { SettingsAttributeCategoryList } from '../../data/settings/SettingsAttributeCategoryList';
import { SettingsAttributeList } from '../../data/settings/SettingsAttributeList';
import { SettingsEmployeeList } from '../../data/settings/SettingsEmployeeList';
import { SettingsGroupList } from '../../data/settings/SettingsGroupList';
import { SettingsIntegrationList } from '../../data/settings/SettingsIntegrationList';
import { SettingsJobList } from '../../data/settings/SettingsJobList';
import { SettingsManagerList } from '../../data/settings/SettingsManagerList';
import { SettingsRegulationDomainList } from '../../data/settings/SettingsRegulationDomainList';
import { SettingsTasksList } from '../../data/settings/SettingsTasksList';
import { CurrentUser } from '../../data/users/CurrentUser';
import AttributesIcon from '../../resources/images/icons-attribute.svg';
import JobsIcon from '../../resources/images/icons-briefcase.svg';
import GroupsIcon from '../../resources/images/icons-departments.svg';
import BillingIcon from '../../resources/images/icons-invoice.svg';
import CompanyIcon from '../../resources/images/icons-office.svg';
import EmployeesIcon from '../../resources/images/icons-users-1.svg';
import { AppPages } from '../../router';
import SettingChecklistRoutes from '../../router/SettingChecklistRoutes';
import SettingStandardsLibraryRoutes from '../../router/SettingStandardsLibraryRoutes';
import { AbstractView, AbstractViewProps } from '../AbstractPage';
import { BillingDetailsDialog } from '../billing/BillingDetailsDialog';
import { BillingPlansDialog } from '../billing/BillingPlansDialog';
import { CreditCardDetailsDialog } from '../billing/CreditCardDetailsDialog';
import { SettingsRegulationsView } from '../regulations/SettingsRegulationsView';
import { SettingsTasksView } from '../tasks/SettingsTasksView';
import { AddEditAttributeView } from './AddEditAttributeView';
import { AddEditCategoryView } from './AddEditCategoryView';
import { SettingsAttributesView } from './SettingsAttributesView';
import { SettingsBillingView } from './SettingsBillingView';
import { SettingsCompanyView } from './SettingsCompanyView';
import { SettingsEmployeesView } from './SettingsEmployeesView';
import { SettingsGroupsView } from './SettingsGroupsView';
import { SettingsJobsView } from './SettingsJobsView';
import { SettingsLandingView } from './SettingsLandingView';

// Type definitions for Settings component
export interface SettingsSubPageConfig {
  name: string;
  nameKey?: string;
  icon: React.ReactNode;
  paths: string[];
  component: React.ComponentType<Record<string, unknown>>;
  hasAccess: (user: CurrentUser | null, organization: Organization | null) => boolean;
  isDefault?: (user: CurrentUser | null) => boolean;
  isShowInSideBar?: boolean;
  exact?: boolean;
  order: number;
}

export interface SettingsProps extends AbstractViewProps {
  organization: Organization | null;
  user: CurrentUser | null;
  theme: typeof Theme;
  landingEnabled?: boolean;
  onChangeCurrentUserFromSettings?: (employee: { employeeId: string }) => void;
  onDemoSwitch?: (e: React.MouseEvent | null, demo: boolean) => void;
  onContactUs?: (e: React.MouseEvent | null, redirect: string) => void;
  onChange?: (change: SettingsChangeEvent) => void;
  onNavigation?: (
    e: React.MouseEvent,
    callback?: ((e: React.MouseEvent) => void) | null
  ) => boolean;
  onBlockNavigation?: (
    block: boolean,
    location: string | null,
    callback?: ((e: React.MouseEvent) => void) | null
  ) => void;
  onAllowNavigation?: (e: React.MouseEvent) => void;
  // Navigation props from withNavigate HOC
  navigate?: (to: string, options?: { replace?: boolean; state?: any }) => void;
  location?: any;
  params?: Record<string, string>;
  history?: any;
  match?: any;
}

export interface SettingsChangeEvent {
  type: string;
  data?: unknown;
}

export interface SettingsState {
  orgSettings: OrganizationSettings | null;
  categories: SettingsAttributeCategoryList | null;
  attributes: SettingsAttributeList | null;
  jobs: SettingsJobList | null;
  courses: SettingsCourseList | null;
  integration: SettingsIntegrationList | null;
  employees: SettingsEmployeeList | null;
  managers: SettingsManagerList | null;
  groups: SettingsGroupList | null;
  billingDetails: BillingSettings | null;
  billingPlans: BillingPlans | null;
  billingInvoices: BillingInvoices | null;
  showAddEditAttributeDialog: boolean;
  addAttributeCallback: ((attr: SettingsAttribute) => void) | null;
  showAddEditCategoryDialog: boolean;
  addCategoryCallback: ((cat: SettingsAttributeCategory) => void) | null;
  categoryError: string | null;
  attributeError: string | null;
  taskError: string | null;
  courseError: string | null;
  showLanding: boolean;
  newCategory: SettingsAttributeCategory | null;
  newAttribute: SettingsAttribute | null;
  showBillingPlans: boolean;
  showBillingDetails: boolean;
  showCreditCardDetails: boolean;
  adminError: string | null;
  errorSnackbar: string | null;
  tasks: SettingsTasksList | null;
  listRegulationRegion: SettingsRegulationRegionList | null;
  listRegulationDomain: SettingsRegulationDomainList | null;
  listAllRegulationDomain: SettingsRegulationDomainList | null;
  listRegulationCategory: SettingsRegulationCategoryList | null;
  listRegulation: SettingsRegulationList | null;
  groupMembers: GroupMemberSelectorList | null;
  isHaveOneItemInLastPageInTasks: boolean;
  faculties?: SettingCourseFacultyList | null;
}

export interface SelectedAttribute {
  attributeDefinitionId: string;
}

export interface JobApiDto {
  jobDefinitionId: string;
}

export interface Invoice {
  invoiceId: string;
}

export interface Course {
  id: string;
  recurrence?: number;
}

export interface RegulationParams {
  name?: string;
  description?: string;
  regulationCategoryId?: string;
}

export class Settings extends AbstractView<SettingsProps> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    landingEnabled: false,
  };

  static subPages = {
    jobs: {
      name: 'Jobs',
      nameKey: 'settingsNav.jobs',
      icon: <JobsIcon />,
      paths: [
        '/settings/jobs',
        '/settings/jobs/builder',
        '/settings/jobs/new',
        '/settings/jobs/edit/:id',
        '/settings/jobs/edit/:id/competencies',
        '/settings/jobs/assign-manager/:id',
        '/settings/jobs/assign-employee/:id',
      ],
      component: SettingsJobsView,
      // Open to ANY logged-in user (org not inactive). What each user sees is scope-aware
      // server-side — admins get org jobs, non-admins get only their own personal jobs — so opening
      // the route to everyone leaks nothing. Sibling sub-pages keep their own isOrganizationAdmin
      // gate. The Manage nav item stays hidden until job #1 via settings.isVisibleInNav.
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        !!user && organization?.status !== Organization.status.inactive,
      isDefault: (_user: CurrentUser | null) => true,
      isShowInSideBar: true,
      order: 2,
    },
    employees: {
      name: 'Employees',
      nameKey: 'settingsNav.employees',
      icon: <EmployeesIcon />,
      paths: ['/settings/employees', '/settings/employees/new', '/settings/employees/edit/:id'],
      component: SettingsEmployeesView,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user &&
        user.isOrganizationAdmin &&
        organization?.status !== Organization.status.inactive &&
        organization?.isEnabledCoreFeatures,
      isShowInSideBar: true,
      order: 1,
    },
    groups: {
      name: 'Groups',
      nameKey: 'settingsNav.groups',
      icon: <GroupsIcon />,
      paths: ['/settings/groups', '/settings/groups/new', '/settings/groups/edit/:id'],
      component: SettingsGroupsView,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user && user.isOrganizationAdmin && organization?.status !== Organization.status.inactive,
      isShowInSideBar: true,
      order: 6,
    },
    competencies: {
      name: 'Competencies',
      nameKey: 'settingsNav.competencies',
      icon: <AttributesIcon />,
      paths: [
        '/settings/competencies',
        '/settings/competencies/new',
        '/settings/competencies/new/parent',
        '/settings/competencies/edit/:id',
        '/settings/competencies/categories',
        '/settings/competencies/categories/new',
        '/settings/competencies/categories/edit/:id',
      ],
      component: SettingsAttributesView,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user &&
        user.isOrganizationAdmin &&
        organization?.status !== Organization.status.inactive &&
        organization?.isEnabledCoreFeatures,
      isShowInSideBar: true,
      order: 4,
    },
    regulations: {
      name: 'Regulations',
      nameKey: 'settingsNav.regulations',
      icon: <AttributesIcon />,
      paths: ['/settings/regulations'],
      component: SettingsRegulationsView,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user &&
        user.isOrganizationAdmin &&
        organization?.status !== Organization.status.inactive &&
        organization?.isEnabledCoreFeatures,
      isShowInSideBar: true,
      order: 7,
    },
    tasks: {
      name: 'Tasks',
      nameKey: 'settingsNav.tasks',
      icon: <AttributesIcon />,
      paths: ['/settings/tasks'],
      component: SettingsTasksView,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user &&
        user.isOrganizationAdmin &&
        organization?.status !== Organization.status.inactive &&
        organization?.isEnabledCoreFeatures,
      isShowInSideBar: true,
      order: 3,
    },
    learning: {
      name: 'Training',
      nameKey: 'settingsNav.training',
      icon: <AttributesIcon />,
      paths: ['/settings/learning'],
      component: ManageRoutes,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user &&
        ((user.isOrganizationAdmin && !!organization?.enabledFeatures?.Training) ||
          user.isTrainingManager) &&
        organization?.status !== Organization.status.inactive,
      isShowInSideBar: true,
      exact: false,
      order: 5,
    },
    // training: {
    //   name: 'Training',
    //   nameKey: 'settingsNav.training',
    //   icon: <AttributesIcon />,
    //   paths: ['/settings/training'],
    //   component: SettingsTrainingView,
    //   hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
    //     user && user.isOrganizationAdmin && organization?.status !== Organization.status.inactive,
    //   isShowInSideBar: true,
    //   exact: false,
    //   order: 5,
    // },
    company: {
      name: 'Company',
      nameKey: 'settingsNav.company',
      icon: <CompanyIcon />,
      paths: [
        '/settings/company',
        '/settings/company/edit',
        '/settings/company/address/new',
        '/settings/company/address/edit',
        '/settings/company/locations/new',
        '/settings/company/locations/edit/:id',
      ],
      component: SettingsCompanyView,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user && user.isOrganizationAdmin && organization?.status !== Organization.status.inactive,
      isShowInSideBar: true,
      order: 10,
    },
    billing: {
      name: 'Billing',
      nameKey: 'settingsNav.billing',
      icon: <BillingIcon />,
      paths: [
        '/settings/billing',
        '/settings/billing/history',
        '/settings/billing/payment',
        '/settings/billing/plan',
        '/settings/billing/details',
        '/settings/billing/payment/update-card',
      ],
      component: SettingsBillingView,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user && user.isBillingUser && organization && !organization.isDemo,
      isDefault: (user: CurrentUser | null) =>
        !!(user && !user.isOrganizationAdmin && user.isBillingUser),
      isShowInSideBar: true,
      order: 11,
    },

    // define all routes that have exact = true here
    standardLibrary: {
      name: 'Klayo Intelligence',
      nameKey: 'settingsNav.standardLibrary',
      icon: <AttributesIcon />,
      paths: ['/settings/standards-library'],
      component: SettingStandardsLibraryRoutes,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user &&
        user.isOrganizationAdmin &&
        organization?.status !== Organization.status.inactive &&
        organization?.enableStandardsLibrary &&
        user.isDeveloper,
      isShowInSideBar: true,
      exact: false,
      order: 8,
    },
    checklists: {
      name: 'Checklists',
      nameKey: 'settingsNav.checklists',
      icon: <AttributesIcon />,
      paths: ['/settings/checklists'],
      component: SettingChecklistRoutes,
      hasAccess: (user: CurrentUser | null, organization: Organization | null) =>
        user &&
        user.isOrganizationAdmin &&
        organization?.status !== Organization.status.inactive &&
        !!organization?.enabledFeatures?.Training,
      isShowInSideBar: true,
      exact: false,
      order: 9,
    },
  };

  constructor(props: SettingsProps) {
    super(props, AppPages.settings);

    this.state = {
      orgSettings: null,
      categories: null,
      attributes: null,
      jobs: null,
      courses: null,
      integration: null,
      employees: null,
      managers: null,
      groups: null,
      billingDetails: null,
      billingPlans: null,
      billingInvoices: null,
      showAddEditAttributeDialog: false,
      addAttributeCallback: null,
      showAddEditCategoryDialog: false,
      addCategoryCallback: null,
      categoryError: null,
      attributeError: null,
      taskError: null,
      courseError: null,
      showLanding:
        (!Utils.checkLocalStorageOption('hideSettingsLanding') ||
          this.hasQuery('showLanding=true')) &&
        props.user &&
        props.user.isOrganizationAdmin &&
        props.organization &&
        !props.organization.isDemo,
      newCategory: null,
      newAttribute: null,
      showBillingPlans: false,
      showBillingDetails: false,
      showCreditCardDetails: false,
      adminError: null,
      errorSnackbar: null,
      tasks: [],
      listRegulationRegion: [],
      listRegulationDomain: [],
      listAllRegulationDomain: [],
      listRegulationCategory: [],
      listRegulation: [],
      groupMembers: [],
      isHaveOneItemInLastPageInTasks: false,
    };
  }

  onViewChange(_location: any): void {
    if (this.pathEquals('/settings')) this.resetErrors();
    if (this.hasQuery('showLanding=true')) this.setState({ showLanding: true });
  }

  resetErrors() {
    this.setState({ categoryError: null, attributeError: null });
  }

  onShowNewAttributeDialog(
    _e: React.MouseEvent | null,
    value: string,
    addAttributeCallback: (attr: SettingsAttribute) => void
  ): void {
    const newAttribute = new SettingsAttribute();
    newAttribute.name = value;
    this.setState({
      showAddEditAttributeDialog: true,
      attributeError: null,
      addAttributeCallback,
      newAttribute,
    });
  }

  onCancelAddEditAttribute(_e?: React.MouseEvent): void {
    this.setState({ showAddEditAttributeDialog: false, attributeError: null });
  }

  onShowNewCategoryDialog(
    _e: SyntheticEvent | null,
    value: string,
    addCategoryCallback: (cat: SettingsAttributeCategory) => void
  ): void {
    const newCategory = new SettingsAttributeCategory();
    newCategory.name = value;
    this.setState({
      showAddEditCategoryDialog: true,
      categoryError: null,
      addCategoryCallback,
      newCategory,
    });
  }

  onCancelAddEditCategory(_e?: React.MouseEvent): void {
    this.setState({ showAddEditCategoryDialog: false, categoryError: null });
  }

  onDeleteVacancy(
    _e: React.MouseEvent,
    job: { toApiDto: () => unknown; jobDefinitionId: string }
  ): Promise<void> {
    const { jobs } = this.state as any;

    this.context?.setLoading?.('addjob', true);

    return new Promise((resolve, reject) => {
      axiosClient
        .post('/Job', job.toApiDto())
        .then((response: AxiosResponse) => {
          jobs?.addVacancy(job.jobDefinitionId, response.data);
          resolve();
        })
        .catch((e: Error) => {
          //this.setState({ jobError: ErrorModel.parseServerError(e) });
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('addjob', false);
        });
    });
  }

  onLoadAttributes(
    _updateCache: boolean,
    searchText: string,
    pageSize?: number,
    orderBy?: string,
    categoryIds?: string[],
    pageNumber?: number
  ): void {
    // option for some api use list all with this api
    const pagingOptions = {
      pageNumber: pageNumber || 1,
      searchText,
      orderBy,
      categoryIds,
      pageSize: pageSize || SettingsAttributeList.defaultPageSize,
    };

    this.context?.setLoading?.('attributes', true);

    // get setting attributes data then set to attributes state
    SettingsAttributeList.getWithPaging(this.context as any, pagingOptions)
      .then((attributes) => {
        this.setState({ attributes });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('attributes', false));
  }

  onLoadAttributesPaging(
    pageNumber: number,
    pageSize: number,
    searchText: string,
    orderBy: string
  ): void {
    this.context?.setLoading?.('attributes', true);

    SettingsAttributeList.getWithPaging(this.context as any, {
      pageNumber: pageNumber,
      searchText,
      orderBy,
      pageSize: pageSize || SettingsAttributeList.defaultPageSize,
    })
      .then((attributes) => {
        this.setState({ attributes });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('attributes', false));
  }

  onLoadAttributesPageSize(
    pageNumber: number,
    pageSize: number,
    searchText: string,
    orderBy: string,
    categoryIds: string[]
  ) {
    this.context?.setLoading?.('attributes', true);

    SettingsAttributeList.getWithPaging(this.context, {
      pageNumber: pageNumber,
      searchText,
      orderBy,
      pageSize: pageSize || SettingsAttributeList.defaultPageSize,
      categoryIds: categoryIds,
    })
      .then((attributes) => {
        this.setState({ attributes });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('attributes', false));
  }

  onLoadCategoriesPaging(
    pageNumber: number,
    pageSize: number,
    searchText: string,
    orderBy: string
  ): void {
    this.context?.setLoading?.('categories', true);

    SettingsAttributeCategoryList.getWithPaging(this.context as any, {
      pageNumber: pageNumber,
      searchText: searchText,
      orderBy,
      pageSize: pageSize || SettingsAttributeCategoryList.defaultPageSize,
    })
      .then((categories) => {
        this.setState({ categories });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('categories', false));
  }

  onLoadCategoriesPageSize(
    pageNumber: number,
    pageSize: number,
    searchText: string,
    orderBy: string
  ): void {
    this.context?.setLoading?.('categories', true);

    SettingsAttributeCategoryList.getWithPaging(this.context as any, {
      pageNumber: pageNumber,
      searchText: searchText,
      orderBy,
      pageSize: pageSize || SettingsAttributeCategoryList.defaultPageSize,
    })
      .then((categories) => {
        this.setState({ categories });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('categories', false));
  }

  onLoadCategories(
    _updateCache: boolean,
    searchText: string,
    pageSize?: number,
    orderBy?: string | null,
    pageNumber?: number
  ): void {
    this.context?.setLoading?.('categories', true);

    const pagingOptions = {
      pageNumber: pageNumber || 1,
      searchText,
      orderBy,
      pageSize: pageSize || SettingsAttributeCategoryList.defaultPageSize,
    };

    SettingsAttributeCategoryList.getWithPaging(this.context as any, pagingOptions)
      .then((categories) => {
        this.setState({ categories });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('categories', false));
  }

  onLoadIntegration(
    _updateCache: boolean,
    searchText: string,
    pageSize: number,
    orderBy: string
  ): void {
    this.context?.setLoading?.('integration', true);

    const pagingOptions = {
      pageNumber: 1,
      searchText,
      orderBy,
      pageSize: pageSize || SettingsIntegrationList.defaultPageSize,
    };

    SettingsIntegrationList.getWithPaging(this.context, pagingOptions)
      .then((integration) => {
        this.setState({ integration });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('integration', false));
  }

  async onLoadTrainingFaculties(): Promise<unknown[] | undefined> {
    this.context?.setLoading?.('faculties', true);
    try {
      const listFaculties = (await SettingCourseFacultyList.onGetData(this.context, {
        apiUrl: '/Integration/Faculties',
      })) as { faculties: unknown[] };
      this.setState({ faculties: listFaculties.faculties });
      this.context?.setLoading?.('faculties', false);
      return listFaculties.faculties;
    } catch (error) {
      this.context?.setLoading?.('faculties', false);
    }
  }

  onLoadTrainingCourse(
    searchText: string,
    pageSize: number,
    orderBy: string,
    integrationId: string,
    facultyId: string
  ): void {
    this.context?.setLoading?.('courses', true);

    const pagingOptions = {
      pageNumber: 1,
      orderBy,
      searchText: searchText,
      integrationId,
      facultyId,
      pageSize: pageSize || SettingsCourseList.defaultPageSize,
    };

    // get setting courses data then set to courses state
    SettingsCourseList.getWithPaging(this.context as any, pagingOptions)
      .then((courses) => {
        this.setState({ courses });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('courses', false));
  }

  onLoadCoursesPaging(
    pageNumber: number,
    pageSize: number,
    searchText: string,
    orderBy: string,
    integrationId: string
  ): void {
    this.context?.setLoading?.('courses', true);

    SettingsCourseList.getWithPaging(this.context as any, {
      pageNumber: pageNumber,
      orderBy,
      searchText: searchText,
      integrationId,
      pageSize: pageSize || SettingsCourseList.defaultPageSize,
    })
      .then((courses) => {
        this.setState({ courses });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('courses', false));
  }

  onLoadCoursesPageSize(
    pageNumber: number,
    pageSize: number,
    searchText: string,
    orderBy: string,
    integrationId: string
  ): void {
    this.context?.setLoading?.('courses', true);

    SettingsCourseList.getWithPaging(this.context as any, {
      pageNumber: pageNumber,
      orderBy,
      searchText: searchText,
      integrationId,
      pageSize: pageSize || SettingsCourseList.defaultPageSize,
    })
      .then((courses) => {
        this.setState({ courses });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('courses', false));
  }

  onSaveCourseRecurrence(id: string, recurrence: number): Promise<void> {
    this.context?.setLoading?.('courses', true);

    const updateData = {
      id,
      recurrence,
    };

    return new Promise((resolve, reject) => {
      axiosClient
        .put('/Integration/Course/UpdateCourse', updateData)
        .then((response: unknown) => {
          const { id, recurrence } = get(response, 'data.integrationCourse', {}) as {
            id: string;
            recurrence: number;
          };
          const { courses } = this.state as { courses: { courses: Course[] } };
          const updateCourses = courses?.courses.map((course: Course) => {
            if (course.id === id) course.recurrence = recurrence;
            return course;
          });

          this.setState({ courses: { ...this.state.courses, courses: updateCourses } });
          // this.onLoadTrainingCourse(true);
          resolve();
        })
        .catch((e: unknown) => {
          // this.setState({ courseError: ErrorModel.parseServerError(e) });
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('courses', false);
        });
    });
  }

  async onLoadTasks(
    searchText: string,
    pageNumber: number,
    pageSize: number,
    orderBy: string
  ): Promise<void> {
    this.context?.setLoading?.('tasks', true);
    const pagingOptions = {
      pageNumber,
      orderBy,
      searchText: searchText,
      pageSize: pageSize || SettingsTasksList.defaultPageSize,
    };

    // get setting tasks data then set to tasks state
    SettingsTasksList.getWithPaging(this.context, pagingOptions)
      .then((tasks) => {
        const taskData = tasks;
        if (taskData && taskData.tasks && taskData.tasks.length === 1 && !taskData.hasNext) {
          this.setState({ isHaveOneItemInLastPageInTasks: true });
        } else {
          this.setState({ isHaveOneItemInLastPageInTasks: false });
        }

        this.setState({ tasks: tasks as any });
      })
      .catch(() => {})
      .finally(() => this.context?.setLoading?.('tasks', false));
  }

  onCreateTask(taskName: string, allSelectedAttributes: SelectedAttribute[]): Promise<unknown> {
    this.context?.setLoading?.('tasks', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .post('/Regulation/Task', {
          name: taskName,
          competencyIds: allSelectedAttributes.map(
            (a: SelectedAttribute) => a.attributeDefinitionId
          ),
        })
        .then((response: unknown) => {
          resolve(response);
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('tasks', false);
        });
    });
  }

  onEditTask(
    regulationTaskId: string,
    taskName: string,
    allSelectedAttributes: SelectedAttribute[]
  ): Promise<void> {
    this.context?.setLoading?.('tasks', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .put('/Regulation/Task', {
          regulationTaskId,
          name: taskName,
          competencyIds: allSelectedAttributes.map(
            (a: SelectedAttribute) => a.attributeDefinitionId
          ),
        })
        .then(() => {
          resolve();
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('tasks', false);
        });
    });
  }

  async onGetListRegulationRegion(search?: string): Promise<unknown[] | undefined> {
    this.context?.setLoading?.('regulationRegion', true);
    try {
      const searchParams = new URLSearchParams();

      if (search) {
        searchParams.set('searchTerm', search);
      }

      const listRegulationRegion = (await SettingsRegulationRegionList.onGetData(this.context, {
        apiUrl: `/Regulation/Region/List?${searchParams}`,
      })) as { regions: unknown[] };
      this.setState({ listRegulationRegion: listRegulationRegion.regions });
      this.context?.setLoading?.('regulationRegion', false);
      return listRegulationRegion.regions;
    } catch (error) {
      this.context?.setLoading?.('regulationRegion', false);
    }
  }

  async onGetListRegulationDomain(
    regionId: string,
    search: string | null
  ): Promise<unknown[] | undefined> {
    let url = '';
    if (search === undefined || search === null || search === '') {
      url = `/Regulation/Region/${regionId}/Domain/List`;
    } else {
      url = `/Regulation/Region/${regionId}/Domain/List?searchTerm=${search}`;
    }
    this.context?.setLoading?.('regularDomains', true);
    try {
      const listRegulationDomain = await SettingsRegulationDomainList.onGetData(this.context, {
        apiUrl: url,
      });
      this.setState({ listRegulationDomain: listRegulationDomain.domains });
      if (search === undefined || search === null || search === '') {
        this.setState({ listAllRegulationDomain: listRegulationDomain.domains });
      }
      this.context?.setLoading?.('regularDomains', false);
      return listRegulationDomain.domains;
    } catch (error) {
      this.context?.setLoading?.('regularDomains', false);
    }
  }

  async onGetListRegulationCategory(
    domainId: string,
    search: string | null
  ): Promise<unknown[] | undefined> {
    let url = '';
    if (search === undefined || search === null || search === '') {
      url = `/Regulation/Domain/${domainId}/Category/List`;
    } else {
      url = `/Regulation/Domain/${domainId}/Category/List?searchTerm=${search}`;
    }
    this.context?.setLoading?.('regularCategories', true);

    try {
      const listRegulationCategory = (await SettingsRegulationCategoryList.onGetData(this.context, {
        apiUrl: url,
      })) as { categories: unknown[] };
      this.setState({ listRegulationCategory: listRegulationCategory.categories });
      this.context?.setLoading?.('regularCategories', false);
      return listRegulationCategory.categories;
    } catch (error) {
      this.context?.setLoading?.('regularCategories', false);
    }
  }

  async onGetListRegulationByCategoryId(
    categoryId: string,
    search: string | null
  ): Promise<unknown[] | undefined> {
    let url = '';
    if (search === undefined || search === null || search === '') {
      url = `/Regulation/Category/${categoryId}/Regulations`;
    } else {
      url = `/Regulation/Category/${categoryId}/Regulations?searchTerm=${search}`;
    }
    this.context?.setLoading?.('regularRegulations', true);

    try {
      const listRegulation = (await SettingsRegulationList.onGetData(this.context as any, {
        apiUrl: url,
      })) as { regulations: unknown[] };
      this.setState({ listRegulation: listRegulation.regulations });
      this.context?.setLoading?.('regularRegulations', false);
      return listRegulation.regulations;
    } catch (error) {
      this.context?.setLoading?.('regularRegulations', false);
    }
  }

  onCreateRegulationDomain(domain: string, regionId: string): Promise<unknown> {
    this.context?.setLoading?.('regularDomains', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .post(`/Regulation/Domain`, {
          name: trim(domain),
          regionId: regionId,
        })
        .then((response: { data: unknown }) => {
          resolve(response.data);
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularDomains', false);
        });
    });
  }

  onUpdateRegulationDomain(domain: string, domainId: string, regionId: string): Promise<unknown> {
    this.context?.setLoading?.('regularDomains', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .put(`/Regulation/Domain`, {
          name: trim(domain),
          regulationDomainId: domainId,
          regulationRegionId: regionId,
        })
        .then((response: { data: unknown }) => {
          resolve(response.data);
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularDomains', false);
        });
    });
  }

  onCreateRegulationCategory(domainId: string, category: string): Promise<unknown> {
    this.context?.setLoading?.('regularCategories', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .post(`/Regulation/Category`, {
          name: trim(category),
          regulationDomainId: domainId,
        })
        .then((response: { data: unknown }) => {
          resolve(response.data);
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularCategories', false);
        });
    });
  }

  onUpdateRegulationCategory(
    category: string,
    categoryId: string,
    regulationDomainId: string
  ): Promise<unknown> {
    this.context?.setLoading?.('regularCategories', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .put(`/Regulation/Category`, {
          name: trim(category),
          regulationCategoryId: categoryId,
          regulationDomainId: regulationDomainId,
        })
        .then((response: { data: unknown }) => {
          resolve(response.data);
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularCategories', false);
        });
    });
  }

  async onSaveRegulation(params: RegulationParams): Promise<unknown> {
    this.context?.setLoading?.('regularRegulations', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .post(`/Regulation/Regulation`, params)
        .then((response: { data: unknown }) => {
          resolve(response.data);
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularRegulations', false);
        });
    });
  }

  async onUpdateRegulation(params: RegulationParams, regulationId: string): Promise<unknown> {
    this.context?.setLoading?.('regularRegulations', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .put(`/Regulation/Regulation`, {
          ...params,
          regulationId,
        })
        .then((response: { data: unknown }) => {
          resolve(response.data);
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularRegulations', false);
        });
    });
  }

  onDeleteTask(regulationTaskId: string): Promise<void> {
    this.context?.setLoading?.('tasks', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .delete('/Regulation/Task/' + regulationTaskId)
        .then(() => {
          resolve();
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('tasks', false);
        });
    });
  }

  async onDeleteDomain(domainId: string): Promise<void> {
    this.context?.setLoading?.('regularDomains', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .delete(`/Regulation/Domain`, {
          data: {
            regulationDomainId: domainId,
          },
        })
        .then(() => {
          resolve();
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularDomains', false);
        });
    });
  }

  async onDeleteCategory(categoryId: string): Promise<void> {
    this.context?.setLoading?.('regularCategories', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .delete(`/Regulation/Category`, {
          data: {
            categoryId: categoryId,
          },
        })
        .then(() => {
          resolve();
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularCategories', false);
        });
    });
  }

  async onDeleteRegulation(regulationId: string): Promise<void> {
    this.context?.setLoading?.('regularRegulations', true);
    return new Promise((resolve, reject) => {
      axiosClient
        .delete(`/Regulation/Regulation`, {
          data: {
            regulationId: regulationId,
          },
        })
        .then(() => {
          resolve();
        })
        .catch((e: unknown) => {
          reject(e);
        })
        .finally(() => {
          this.context?.setLoading?.('regularRegulations', false);
        });
    });
  }

  onLoadJobs(
    _updateCache: boolean,
    searchText: string,
    pageSize: number,
    pageNumber?: number
  ): void {
    this.context?.setLoading?.('jobs', true);

    const pagingOptions = {
      pageNumber: pageNumber || 1,
      pageSize: pageSize || SettingsJobList.defaultPageSize,
      searchText: searchText,
    };

    // get setting job data then set to jobs state
    SettingsJobList.getWithPaging(this.context, pagingOptions)
      .then((jobs) => {
        this.setState({ jobs });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('jobs', false));
  }

  onLoadJobsPaging(pageNumber: number, pageSize: number, searchText: string): void {
    this.context?.setLoading?.('jobs', true);

    SettingsJobList.getWithPaging(this.context, {
      pageNumber: pageNumber,
      pageSize: pageSize || SettingsJobList.defaultPageSize,
      searchText: searchText,
    })
      .then((jobs) => {
        this.setState({ jobs });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('jobs', false));
  }

  onLoadJobsPageSize(pageNumber: number, pageSize: number, searchText: string): void {
    this.context?.setLoading?.('jobs', true);

    SettingsJobList.getWithPaging(this.context, {
      pageNumber: pageNumber,
      searchText: searchText,
      pageSize: pageSize || SettingsJobList.defaultPageSize,
    })
      .then((jobs) => {
        this.setState({ jobs });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('jobs', false));
  }

  onLoadGroupMembers(_updateCache: boolean, searchText: string, pageSize: number): void {
    this.context?.setLoading?.('groupMembers', true);
    GroupMemberSelectorList.getWithPaging(this.context, {
      pageNumber: 1,
      pageSize: pageSize || GroupMemberSelectorList.defaultPageSize,
      searchText: searchText,
    })
      .then((groups) => {
        this.setState({ groupMembers: groups.employees });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('groupMembers', false));
  }

  onLoadEmployees(
    _updateCache: boolean,
    searchText: string,
    pageSize: number,
    status: number | null,
    orderBy: string,
    includeInactive: boolean,
    accessLevels?: number[],
    roles?: number[]
  ): void {
    this.context?.setLoading?.('employees', true);

    // get setting employees data then set to employees state
    SettingsEmployeeList.getWithPaging(this.context, {
      pageNumber: 1,
      searchText,
      orderBy,
      status,
      pageSize: pageSize || SettingsEmployeeList.defaultPageSize,
      includeInactive,
      accessLevels,
      roles,
    })
      .then((employees) => {
        this.setState({ employees });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('employees', false));
  }

  onLoadEmployeesPaging(
    pageNumber: number,
    pageSize: number,
    searchText: string,
    status: number | null,
    orderBy: string,
    includeInactive: boolean,
    accessLevels?: number[],
    roles?: number[]
  ): void {
    this.context?.setLoading?.('employees', true);

    SettingsEmployeeList.getWithPaging(this.context, {
      pageNumber: pageNumber,
      searchText,
      status,
      orderBy,
      pageSize: pageSize || SettingsEmployeeList.defaultPageSize,
      includeInactive,
      accessLevels,
      roles,
    })
      .then((employees) => {
        this.setState({ employees });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('employees', false));
  }

  onLoadEmployeesPageSize(
    pageNumber: number,
    pageSize: number,
    searchText: string,
    status: number | null,
    orderBy: string,
    includeInactive: boolean,
    accessLevels?: number[],
    roles?: number[]
  ): void {
    this.context?.setLoading?.('employees', true);

    // get setting employees data then set to employees state
    SettingsEmployeeList.getWithPaging(this.context, {
      pageNumber: pageNumber,
      searchText,
      status,
      orderBy,
      pageSize: pageSize || SettingsEmployeeList.defaultPageSize,
      includeInactive,
      accessLevels,
      roles,
    })
      .then((employees) => {
        this.setState({ employees });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('employees', false));
  }

  onLoadManagers(updateCache: boolean): void {
    this.context?.setLoading?.('managers', true);

    SettingsManagerList.getSettingsManagerList(this.context, updateCache)
      .then((managers) => {
        this.setState({ managers });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('managers', false));
  }

  onLoadGroups(updateCache: boolean): void {
    this.context?.setLoading?.('groups', true);

    SettingsGroupList.getSettingsGroupList(this.context, updateCache)
      .then((groups) => {
        this.setState({ groups });
      })
      .catch((error) => {})
      .finally(() => this.context?.setLoading?.('groups', false));
  }

  handleNewAttribute(attr: SettingsAttribute, _returnToList: boolean): void {
    const { addAttributeCallback } = this.state;

    this.onCancelAddEditAttribute();
    if (addAttributeCallback) addAttributeCallback(attr);
  }

  onSaveAttribute(
    attribute: SettingsAttribute,
    returnToList?: boolean,
    attributeSearch?: string,
    categorySearch?: string,
    orderBy?: string,
    categoryIds?: string[],
    pageNumber?: number,
    pageSize?: number
  ): Promise<void> {
    const { attributes } = this.state as any;

    this.context?.setLoading?.('savingAttribute', true);

    return new Promise<void>((resolve, reject) => {
      if (attribute.attributeId) {
        axiosClient
          .put('/AttributeDefinition', attribute.toApiDto(), {
            withCredentials: true,
          })
          .then((response: AxiosResponse) => {
            const updatedAttr = attributes?.update(response.data);
            if (updatedAttr) this.handleNewAttribute(updatedAttr, !!returnToList);
            this.onLoadCategories(true, categorySearch || '');
            this.onLoadAttributes(
              true,
              attributeSearch || '',
              pageSize,
              orderBy,
              categoryIds,
              pageNumber
            );
            resolve();
          })
          .catch((e: Error) => {
            this.setState({ attributeError: ErrorModel.parseServerError(e) });
            reject(e);
          })
          .finally(() => {
            this.context?.setLoading?.('savingAttribute', false);
          });
      } else {
        axiosClient
          .post('/AttributeDefinition', attribute.toApiDto())
          .then((response: AxiosResponse) => {
            const newAttr = attributes?.add(response.data);
            if (newAttr) this.handleNewAttribute(newAttr, !!returnToList);
            this.onLoadCategories(true, categorySearch || '');
            this.onLoadAttributes(
              true,
              attributeSearch || '',
              pageSize,
              orderBy,
              categoryIds,
              pageNumber
            );
            resolve();
          })
          .catch((e: Error) => {
            // this.setState({ attributeError: ErrorModel.parseServerError(e) });
            reject(e);
          })
          .finally(() => {
            this.context?.setLoading?.('savingAttribute', false);
          });
      }
    });
  }

  handleNewCategory(cat: SettingsAttributeCategory): void {
    const { addCategoryCallback } = this.state as any;

    this.onCancelAddEditCategory();
    if (addCategoryCallback) addCategoryCallback(cat);
  }

  onSaveCategory(
    category: SettingsAttributeCategory,
    categorySearch: string,
    pageNumber?: number,
    pageSize?: number
  ): Promise<void> {
    this.context?.setLoading?.('savingCategory', true);

    return new Promise<void>((resolve, reject) => {
      if (category.categoryId) {
        axiosClient
          .put('/AttributeDefinition/AttributeCategory', {
            attributeCategoryId: category.categoryId,
            name: category.name,
          })
          .then((response: AxiosResponse) => {
            const updatedCat = new SettingsAttributeCategory(response.data);
            this.handleNewCategory(updatedCat);
            this.onLoadCategories(true, categorySearch, pageSize, null, pageNumber);
            resolve();
          })
          .catch((e: Error) => {
            // this.setState({ categoryError: ErrorModel.parseServerError(e) });
            reject(e);
          })
          .finally(() => {
            this.context?.setLoading?.('savingCategory', false);
          });
      } else {
        axiosClient
          .post('/AttributeDefinition/AttributeCategory', {
            name: category.name,
          })
          .then((response: AxiosResponse) => {
            const newCat = new SettingsAttributeCategory(response.data);
            this.handleNewCategory(newCat);
            this.onLoadCategories(true, categorySearch, pageSize, null, pageNumber);
            resolve();
          })
          .catch((e: Error) => {
            // this.setState({ categoryError: ErrorModel.parseServerError(e) });
            reject(e);
          })
          .finally(() => {
            this.context?.setLoading?.('savingCategory', false);
          });
      }
    });
  }

  onHasUser(user: CurrentUser | null): void {
    if (user && user.isOrganizationAdmin) {
      if (this.hasQuery('showLanding=true')) this.setState({ showLanding: true });

      this.onLoadOrgSettings(true);
    }
  }
  onLoadOrgSettings(cached?: boolean) {
    this.context?.setLoading?.('settings', true);

    OrganizationSettings.load(this.context as any, cached)
      .then((orgSettings) => {
        this.setState({ orgSettings });
      })
      .catch((e) => {
        this.setState({ adminError: ErrorModel.parseServerError(e) });
      })
      .finally(() => this.context?.setLoading?.('settings', false));
  }
  onCompanyNameChange(name: string): void {
    const { organization } = this.props;
    if (organization) {
      organization.name = name;
    }
    this.updateTitle();
  }

  onLoadBillingDetails(updateCache?: boolean): void {
    const { user } = this.props;

    if (user && user.isBillingUser) {
      this.context?.setLoading?.('billing', true);

      BillingSettings.getBillingSettings(this.context, updateCache)
        .then((billingDetails) => {
          if (billingDetails.dataReceived) this.setState({ billingDetails });
        })
        .catch((error) => {})
        .finally(() => this.context?.setLoading?.('billing', false));
    }
  }

  onLoadBillingInvoices(updateCache: boolean): void {
    const { user } = this.props;

    if (user && user.isBillingUser) {
      this.context?.setLoading?.('billingInvoices', true);

      BillingInvoices.getBillingInvoices(this.context, updateCache)
        .then((billingInvoices) => {
          if (billingInvoices.dataReceived) this.setState({ billingInvoices: billingInvoices });
        })
        .catch((error) => {})
        .finally(() => this.context?.setLoading?.('billingInvoices', false));
    }
  }

  onLoadBillingPlans(updateCache?: boolean): void {
    const { user } = this.props;

    if (user && user.isBillingUser) {
      this.context?.setLoading?.('billingPlans', true);

      BillingPlans.getBillingPlans(this.context, updateCache)
        .then((billingPlans) => {
          this.setState({ billingPlans });
        })
        .catch((error) => {})
        .finally(() => this.context?.setLoading?.('billingPlans', false));
    }
  }

  reload() {
    this.onLoadBillingDetails(true);
    this.onLoadBillingInvoices(true);
  }

  onShowBillingPlans() {
    this.setState({ showBillingPlans: true });
  }

  onCloseBillingPlansDialog() {
    const { history } = this.props;
    this.setState({ showBillingPlans: false }, () => this.reload());
    if (history) {
      history.push('/settings/billing');
    }
  }

  onShowBillingDetails() {
    this.setState({ showBillingDetails: true });
  }

  onCloseBillingDetailsDialog() {
    const { history } = this.props;
    this.setState({ showBillingDetails: false }, () => this.reload());
    if (history) {
      history.push('/settings/billing');
    }
  }

  onShowCreditCardDetails() {
    this.setState({ showCreditCardDetails: true });
  }

  onCloseCreditCardDetailsDialog() {
    const { history } = this.props;
    this.setState({ showCreditCardDetails: false }, () => this.reload());
    if (history) {
      history.push('/settings/billing/payment');
    }
  }

  onInvoiceDownload(_e: React.MouseEvent, invoice: Invoice): void {
    this.context?.setLoading?.('downloadingInvoice', true);

    Data.call('get', '/Organization/Invoices/' + invoice.invoiceId, {
      responseType: 'blob',
    })
      .then((response: any) => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', invoice.invoiceId);
        document.body.appendChild(link);
        link.click();
      })
      .catch((_e: any) => {
        this.setState({ errorSnackbar: 'Failed to download invoice' });
      })
      .finally(() => this.context?.setLoading?.('downloadingInvoice', false));
  }

  getPageProps(page: SettingsSubPageConfig): Record<string, unknown> {
    const { billingDetails, billingInvoices } = this.state as any;

    if (page === (Settings.subPages.company as any))
      return { onCompanyNameChange: this.onCompanyNameChange.bind(this) };
    else if (page === (Settings.subPages.competencies as any))
      return {
        onSaveAttribute: this.onSaveAttribute.bind(this),
        onSaveCategory: this.onSaveCategory.bind(this),
      };
    else if (page === (Settings.subPages.billing as any))
      return {
        onLoadBillingDetails: this.onLoadBillingDetails.bind(this),
        onLoadBillingInvoices: this.onLoadBillingInvoices.bind(this),
        billingDetails,
        billingInvoices,
        onShowBillingPlans: this.onShowBillingPlans.bind(this),
        onShowBillingDetails: this.onShowBillingDetails.bind(this),
        onShowCreditCardDetails: this.onShowCreditCardDetails.bind(this),
        onInvoiceDownload: this.onInvoiceDownload.bind(this),
      };
    return {};
  }

  onHideLandingView(_e: React.MouseEvent): void {
    Utils.setLocalStorageOption('hideSettingsLanding', 'true');
    this.setState({ showLanding: false });
  }

  onDataChanged(change: SettingsChangeEvent): void {
    const { onChange } = this.props;
    if (onChange) onChange(change);
  }

  render() {
    if (!this.verifyAccess()) return this.renderAccessDenied();
    const {
      organization,
      user,
      theme,
      location,
      history,
      match,
      params,
      onChangeCurrentUserFromSettings,
      onDemoSwitch,
      onContactUs,
      landingEnabled,
      onNavigation,
      onBlockNavigation,
      onAllowNavigation,
    } = this.props;
    const {
      orgSettings,
      courses,
      tasks,
      listRegulationDomain,
      listAllRegulationDomain,
      listRegulation,
      listRegulationCategory,
      listRegulationRegion,
      integration,
      categories,
      attributes,
      jobs,
      employees,
      managers,
      groups,
      billingPlans,
      adminError,
      groupMembers,
      isHaveOneItemInLastPageInTasks,
    } = this.state;
    const {
      showAddEditCategoryDialog,
      showAddEditAttributeDialog,
      categoryError,
      attributeError,
      showLanding,
      newCategory,
      newAttribute,
      showBillingPlans,
      showBillingDetails,
      showCreditCardDetails,
      billingDetails,
      errorSnackbar,
    } = this.state;
    const root = this.props.location.pathname === '/settings';

    return landingEnabled && showLanding && root ? (
      <SettingsLandingView
        {...this.props}
        allowDemoSwitch={
          organization &&
          (organization.isDemo ||
            (user &&
              user.isOrganizationAdmin &&
              user.hasDemoEmployee &&
              organization.demoOrgStatus !== Organization.status.resetting &&
              theme.isBreakpointUp('md')))
        }
        onHide={this.onHideLandingView.bind(this)}
        onDemoSwitch={onDemoSwitch}
        showNameInGreeting={user && user.isValidFirstName}
      />
    ) : (
      <View
        className='klayo-settings'
        theme={theme}
        fullWidth={false}
        scrollToTopOnMount={true}
        sx={{
          padding: { xs: '56px 0px', sm: '56px 0px' },
          minHeight: '100vh',
          background: { xs: 'var(--white-wh-900)', md: '#f8f8fe' },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {adminError && <Alert severity='error'>{adminError}</Alert>}

        {!adminError && (
          <Routes>
            {(Object.values(Settings.subPages) as any[])
              .filter((i) => user && organization && i.hasAccess(user, organization))
              .map((page: any, index: number) => {
                // Extract relative path from full path (e.g., "/settings/jobs" -> "jobs")
                // React Router v6 nested Routes require relative paths
                const relativePath = page.paths[0].replace(/^\/settings\/?/, '') || '';
                // Add wildcard if:
                // 1. exact is explicitly false (nested route containers), OR
                // 2. there are multiple paths (has sub-routes like /new, /edit/:id)
                const needsWildcard = page.exact === false || page.paths.length > 1;
                const primaryPath = relativePath + (needsWildcard ? '/*' : '');

                const pageElement = React.createElement<any>(page.component, {
                  organization,
                  theme,
                  location,
                  history,
                  match,
                  params,
                  orgSettings,
                  categories,
                  attributes,
                  jobs,
                  courses,
                  tasks,
                  listRegulation,
                  listRegulationCategory,
                  listRegulationRegion,
                  listRegulationDomain,
                  listAllRegulationDomain,
                  integration,
                  employees,
                  user,
                  managers,
                  groups,
                  groupMembers,
                  allowAssignAdminBilling: true,
                  onLoadOrgSettings: this.onLoadOrgSettings.bind(this),
                  onLoadAttributes: this.onLoadAttributes.bind(this),
                  onLoadAttributesPaging: this.onLoadAttributesPaging.bind(this),
                  onLoadAttributesPageSize: this.onLoadAttributesPageSize.bind(this),
                  onLoadCategoriesPaging: this.onLoadCategoriesPaging.bind(this),
                  onLoadCategoriesPageSize: this.onLoadCategoriesPageSize.bind(this),
                  onLoadCategories: this.onLoadCategories.bind(this),
                  onLoadJobs: this.onLoadJobs.bind(this),
                  onLoadJobsPaging: this.onLoadJobsPaging.bind(this),
                  onLoadJobsPageSize: this.onLoadJobsPageSize.bind(this),
                  onLoadIntegration: this.onLoadIntegration.bind(this),
                  onLoadTrainingCourse: this.onLoadTrainingCourse.bind(this),
                  onLoadTrainingFaculties: this.onLoadTrainingFaculties.bind(this),
                  onLoadCoursesPaging: this.onLoadCoursesPaging.bind(this),
                  onLoadCoursesPageSize: this.onLoadCoursesPageSize.bind(this),
                  onLoadTasks: this.onLoadTasks.bind(this),
                  onCreateTask: this.onCreateTask.bind(this),
                  onEditTask: this.onEditTask.bind(this),
                  onGetListRegulationRegion: this.onGetListRegulationRegion.bind(this),
                  onGetListRegulationDomain: this.onGetListRegulationDomain.bind(this),
                  onGetListRegulationCategory: this.onGetListRegulationCategory.bind(this),
                  onGetListRegulationByCategoryId: this.onGetListRegulationByCategoryId.bind(this),
                  onCreateRegulationDomain: this.onCreateRegulationDomain.bind(this),
                  onUpdateRegulationDomain: this.onUpdateRegulationDomain.bind(this),
                  onCreateRegulationCategory: this.onCreateRegulationCategory.bind(this),
                  onUpdateRegulationCategory: this.onUpdateRegulationCategory.bind(this),
                  onSaveRegulation: this.onSaveRegulation.bind(this),
                  onUpdateRegulation: this.onUpdateRegulation.bind(this),
                  onLoadEmployees: this.onLoadEmployees.bind(this),
                  onLoadEmployeesPaging: this.onLoadEmployeesPaging.bind(this),
                  onLoadEmployeesPageSize: this.onLoadEmployeesPageSize.bind(this),
                  onLoadGroupMembers: this.onLoadGroupMembers.bind(this),
                  onSaveCourseRecurrence: this.onSaveCourseRecurrence.bind(this),
                  onLoadManagers: this.onLoadManagers.bind(this),
                  onLoadGroups: this.onLoadGroups.bind(this),
                  onDataChanged: this.onDataChanged.bind(this),
                  onShowNewCategoryDialog: this.onShowNewCategoryDialog.bind(this),
                  onShowNewAttributeDialog: this.onShowNewAttributeDialog.bind(this),
                  onDeleteDomain: this.onDeleteDomain.bind(this),
                  onDeleteCategory: this.onDeleteCategory.bind(this),
                  onDeleteRegulation: this.onDeleteRegulation.bind(this),
                  onBlockNavigation,
                  onAllowNavigation,
                  onNavigation,
                  onContactUs,
                  onChangeCurrentUserFromSettings,
                  errorSnackbar,
                  isHaveOneItemInLastPageInTasks,
                  ...this.getPageProps(page),
                });

                return <Route key={index} path={primaryPath} element={pageElement} />;
              })}
          </Routes>
        )}

        {showAddEditAttributeDialog && (
          <AddEditAttributeView
            theme={theme}
            orgSettings={orgSettings as any}
            dialogMode={true}
            error={attributeError}
            location={location}
            categories={categories as any}
            attributes={attributes as any}
            attribute={newAttribute as any}
            onLoadCategories={this.onLoadCategories.bind(this)}
            onLoadAttributes={this.onLoadAttributes.bind(this)}
            onLoadJob={this.onLoadJobs.bind(this)}
            onShowNewCategoryDialog={this.onShowNewCategoryDialog.bind(this)}
            onBlockNavigation={onBlockNavigation}
            onAllowNavigation={onAllowNavigation}
            onNavigation={onNavigation}
            onSave={this.onSaveAttribute.bind(this)}
            onCancel={this.onCancelAddEditAttribute.bind(this)}
          />
        )}

        {showAddEditCategoryDialog && (
          <AddEditCategoryView
            theme={theme}
            dialogMode={true}
            error={categoryError}
            category={newCategory}
            onSave={this.onSaveCategory.bind(this)}
            onCancel={this.onCancelAddEditCategory.bind(this)}
          />
        )}

        {showBillingPlans && (
          <BillingPlansDialog
            user={user}
            organization={organization}
            theme={theme}
            billingPlans={billingPlans}
            existingBillingDetails={billingDetails}
            onLoadBillingDetails={() => this.onLoadBillingDetails(true)}
            onLoadBillingPlans={() => this.onLoadBillingPlans(true)}
            onContactUs={onContactUs}
            onClose={this.onCloseBillingPlansDialog.bind(this)}
          />
        )}

        {showBillingDetails && (
          <BillingDetailsDialog
            user={user}
            organization={organization}
            theme={theme}
            existingBillingDetails={billingDetails}
            onLoadBillingDetails={() => this.onLoadBillingDetails(true)}
            onLoadBillingPlans={() => this.onLoadBillingPlans(true)}
            onContactUs={onContactUs}
            onClose={this.onCloseBillingDetailsDialog.bind(this)}
          />
        )}

        {showCreditCardDetails && (
          <CreditCardDetailsDialog
            user={user}
            organization={organization}
            theme={theme}
            onClose={this.onCloseCreditCardDetailsDialog.bind(this)}
          />
        )}
      </View>
    );
  }
}
